import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import { type LeadershipOfficerCreationInfo } from 'helpers'
import { type BaseJurisdiction, revertTransformAddress, transformAddress } from 'services'
import { type LeadershipOfficerWithAddressAndUrls } from './leadershipOfficerListService'

export interface LeadershipOfficerCreateResponse {
  id: number
}

export interface LeadershipOfficeJurisdiction extends BaseJurisdiction {
  legOfcId: number
}

export async function createLeadershipOfficer (payload: LeadershipOfficerCreationInfo): Promise<HttpResponse<LeadershipOfficerWithAddressAndUrls>> {
  return await handleHttpRequest(http.post('/api/v1/us/leadership-officers/', transformRequestLeadershipCreate(payload)))
}

export function transformRequestLeadershipCreate (payload: LeadershipOfficerCreationInfo): Record<string, any> {
  return {
    ...payload,
    address: {
      id: payload.address.id,
      legOfcId: payload.address.legOfcId,
      ...revertTransformAddress(payload.address.primary, 'primary'),
      ...revertTransformAddress(payload.address.secondary, 'secondary'),
      mailingAddress: payload.address.mailingAddress,
      displayOrder: payload.address.displayOrder,
      updatedBy: payload.address.updatedBy,
      lastUpdated: payload.address.lastUpdated
    }
  }
}

export async function deleteLeadership (legOfcId: number): Promise<HttpResponse<void>> {
  return await handleHttpRequest(http.delete(`/api/v1/us/leadership-officers/${legOfcId}/`))
}

export async function retireLeadership (legOfcId: number, personId: number): Promise<HttpResponse<LeadershipOfficerWithAddressAndUrls>> {
  const response = await handleHttpRequest<LeadershipOfficerWithAddressAndUrls>(http.delete(`/api/v1/us/leadership-officers/${legOfcId}/${personId}/`))

  if (response.result === null) {
    return response
  }
  formatLeadershipAddress(response.result)

  return response
}

export async function getLeadershipOfficerJurisdiction (legOfcId: number): Promise<HttpResponse<LeadershipOfficeJurisdiction>> {
  return await handleHttpRequest(http.get(`/api/v1/us/leadership-officers/${legOfcId}/jurisdiction/`))
}

export async function updateLeadershipOfficerJurisdiction<T extends LeadershipOfficeJurisdiction> (jurisdiction: T): Promise<HttpResponse<T>> {
  return await handleHttpRequest(http.patch(`/api/v1/us/leadership-officers/${jurisdiction.legOfcId}/jurisdiction/`, jurisdiction))
}

export async function updateLeadershipFullInfo (legOfcId: number, leadership: LeadershipOfficerWithAddressAndUrls): Promise<HttpResponse<LeadershipOfficerWithAddressAndUrls>> {
  const response = await handleHttpRequest<LeadershipOfficerWithAddressAndUrls>(http.put(`/api/v1/us/leadership-officers/${legOfcId}/`, transformRequestLeadership(leadership)))

  if (response.result === null) {
    return response
  }
  formatLeadershipAddress(response.result)

  return response
}

export interface FillLeadershipOptions {
  personId?: number
  firstName?: string
  lastName?: string
}

export async function fillLeadership (legOfcId: number, options: FillLeadershipOptions): Promise<HttpResponse<LeadershipOfficerWithAddressAndUrls>> {
  const hasPersonId = options.personId !== undefined
  const hasPersonData = options.firstName !== undefined || options.lastName !== undefined

  if (!hasPersonId && !hasPersonData) {
    throw new Error('Either personId or person name information (firstName/lastName) must be provided')
  }

  const payload = {
    id: options.personId ?? null,
    firstName: options.firstName ?? null,
    lastName: options.lastName ?? null
  }

  const response = await handleHttpRequest<LeadershipOfficerWithAddressAndUrls>(
    http.post(`/api/v1/us/leadership-officers/${legOfcId}/`, payload)
  )

  if (response.result === null) {
    return response
  }
  formatLeadershipAddress(response.result)

  return response
}

export function transformRequestLeadership (leadership: LeadershipOfficerWithAddressAndUrls): Record<string, any> {
  return {
    ...leadership,
    address: {
      id: leadership.address.id,
      typeId: 0,
      legOfcId: leadership.id,
      ...revertTransformAddress(leadership.address.primary, 'primary'),
      ...revertTransformAddress(leadership.address.secondary, 'secondary'),
      mailingAddress: leadership.address.mailingAddress,
      phones: leadership.address.phones.map(phone => ({
        ...phone,
        id: phone.id != null && phone.id < 0 ? 0 : phone.id
      })),
      displayOrder: leadership.address.displayOrder,
      updatedBy: leadership.address.updatedBy,
      lastUpdated: leadership.address.lastUpdated
    },
    urls: leadership.urls.map(url => ({
      ...url,
      id: url.id != null && url.id < 0 ? 0 : url.id
    }))
  }
}

export function formatLeadershipAddress (result: LeadershipOfficerWithAddressAndUrls): void {
  result.address = {
    id: result.address.id,
    legOfcId: result.id,
    typeId: 0,
    primary: transformAddress(result.address, 'primary'),
    secondary: transformAddress(result.address, 'secondary'),
    mailingAddress: result.address.mailingAddress,
    phones: result.address.phones,
    displayOrder: result.address.displayOrder,
    updatedBy: result.address.updatedBy,
    lastUpdated: result.address.lastUpdated
  }
}
